import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import LetterSlotInput, { getProximityFeedback } from './LetterSlotInput';
import { useTheme } from '../context/ThemeContext';

export default function QuizCard({ question, questionIndex = 0, input, onInputChange, onSubmit, onNext, onShowAnswer, showAnswer, answer, accentWord }) {
    const [proximityMsg, setProximityMsg] = useState(null);
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const cardBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.55)';
    const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,70,140,0.15)';
    const inputBoxBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.45)';
    const questionColor = isDark ? '#e8e8f0' : '#1a1830';
    const skipColor = isDark ? '#e8e8f0' : '#1a1830';
    const skipBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(80,70,140,0.2)';
    const skipBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.4)';
    const skipHoverBorder = isDark ? 'rgba(255,255,255,0.28)' : 'rgba(80,70,140,0.4)';
    const skipHoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)';
    const hintColor = isDark ? '#7878a0' : '#5050a0';
    const hintBorder = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(80,70,140,0.15)';
    const hintHoverColor = isDark ? '#e8e8f0' : '#1a1830';
    const hintHoverBorder = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(80,70,140,0.35)';
    const hintHoverBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.5)';
    const answerBoxBg = isDark ? 'rgba(108,99,255,0.12)' : 'rgba(85,72,224,0.1)';
    const answerBoxBorder = isDark ? 'rgba(108,99,255,0.3)' : 'rgba(85,72,224,0.3)';
    const answerTextColor = isDark ? '#9d97ff' : '#4a40c0';

    useEffect(() => { setProximityMsg(null); }, [questionIndex]);

    const handleSubmit = () => {
        const feedback = getProximityFeedback(input, answer || '');
        setProximityMsg(feedback || null);
        onSubmit();
    };

    let questionNode;
    if (accentWord && typeof question === 'string') {
        const parts = question.split(accentWord);
        questionNode = (<>{parts[0]}<span style={{ background: 'linear-gradient(135deg, #6c63ff, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 800 }}>{accentWord}</span>{parts[1]}</>);
    } else {
        questionNode = question;
    }

    return (
        <motion.div key={questionIndex} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}>
            <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '20px', p: { xs: 3, sm: 4 }, mb: 2, textAlign: 'center' }}>
                <Typography sx={{ color: questionColor, fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.6rem' }, lineHeight: 1.3 }}>
                    {questionNode}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ background: inputBoxBg, border: `1px solid ${cardBorder}`, borderRadius: '16px', px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                    <LetterSlotInput answer={answer || ''} value={input} onChange={onInputChange} onSubmit={handleSubmit} autoFocus disabled={showAnswer} />
                    <AnimatePresence>
                        {proximityMsg && (
                            <motion.div key={proximityMsg.message} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                                <Typography sx={{ color: proximityMsg.level === 'veryClose' ? '#f0a500' : hintColor, fontSize: '0.82rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, textAlign: 'center', mt: 0.5 }}>
                                    {proximityMsg.message}
                                </Typography>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Box>

                <Button fullWidth variant="contained" onClick={handleSubmit} disabled={showAnswer} sx={{
                    borderRadius: '14px', py: 1.6, fontSize: '1rem', fontWeight: 700, fontFamily: "'DM Sans', sans-serif", textTransform: 'none',
                    background: 'linear-gradient(135deg, #6c63ff 0%, #2575fc 100%)', boxShadow: '0 4px 24px rgba(108,99,255,0.35)', letterSpacing: '0.02em',
                    '&:hover': { background: 'linear-gradient(135deg, #7d75ff 0%, #3a85ff 100%)', boxShadow: '0 6px 28px rgba(108,99,255,0.45)', transform: 'translateY(-1px)' },
                    '&.Mui-disabled': { background: 'rgba(108,99,255,0.2)', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)' },
                    transition: 'all 0.2s ease',
                }}>
                    Controleer →
                </Button>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button fullWidth variant="outlined" onClick={onNext} sx={{
                        borderRadius: '12px', py: 1.2, fontSize: '0.9rem', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textTransform: 'none',
                        color: skipColor, borderColor: skipBorder, background: skipBg,
                        '&:hover': { borderColor: skipHoverBorder, background: skipHoverBg },
                    }}>
                        Sla over
                    </Button>

                    {!showAnswer ? (
                        <Button fullWidth variant="outlined" onClick={onShowAnswer} sx={{
                            borderRadius: '12px', py: 1.2, fontSize: '0.9rem', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textTransform: 'none',
                            color: hintColor, borderColor: hintBorder, background: 'transparent',
                            '&:hover': { color: hintHoverColor, borderColor: hintHoverBorder, background: hintHoverBg },
                        }}>
                            Toon antwoord
                        </Button>
                    ) : (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: answerBoxBg, border: `1px solid ${answerBoxBorder}`, borderRadius: '12px', px: 2 }}>
                            <Typography sx={{ color: answerTextColor, fontWeight: 700, fontSize: '0.95rem', fontFamily: "'DM Sans', sans-serif" }}>
                                {answer}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}
